import { CarePhase } from '../../../appModel';
import { appliedHealthCareInflation } from '../../../appliedHealthCareInflation';
import { calcMonthlyCareInflatedValue } from './phaseCarePlanCalculations';
import {
  DAYS_IN_MONTH,
  ONE_ADL_DURATION_MULTIPLIER,
  POLICY_ACTIVATES_AT_ADL_COUNT,
} from './sharedConstants';
import {
  getCareCostAnchorYear,
  getFirstContributingPhaseStartYear,
} from './sharedPolicyConfig';

// Per-row count of "non-2-ADL" days — days where the client has < 2 ADLs
// and so isn't eligible for benefit payment. The ineligible bucket sits
// at the start of the phase. Strategies whose products don't use the
// 2-ADL gate can skip this helper.
export function computeNon2AdlDaysThisRow(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): number {
  const phaseCareDays = Math.round(phaseCarePlan.phaseCareMonthsNeeded) * DAYS_IN_MONTH;
  const clientAdlCount = client.intakeSurvey?.clientAdlReceivedAssistanceCount ?? 0;
  const has2AdlGate =
    phaseCarePlan.carePhase === CarePhase.earlyCare &&
    clientAdlCount < POLICY_ACTIVATES_AT_ADL_COUNT;
  if (!has2AdlGate) return 0;
  const non2AdlBucketEnd = phaseCareDays * ONE_ADL_DURATION_MULTIPLIER;
  const dayStart = monthsIntoPhase * DAYS_IN_MONTH;
  const dayEnd = dayStart + DAYS_IN_MONTH;
  return Math.max(0, Math.min(dayEnd, non2AdlBucketEnd) - Math.max(dayStart, 0));
}

export interface MonthlyCostMetrics {
  nominalCareCost: number;
  effectiveCareCost: number;
  dailyCareCost: number;
  // Years of care-cost inflation applied to this row (the exponent on
  // `(1 + inflationRate)`). Surfaced so the UI can label the inflation
  // breakdown without re-deriving it from session-level terms — a derivation
  // that reads wrong for joint partner rows, whose merged-stream monthIndex
  // and earlier-than-the-session-client onset don't match `terms`.
  careCostInflationYears: number;
  // Calendar year this row's care is projected to occur in (claimant-relative).
  projectedCareYear: number;
}

// Per-month effective care cost. Inflates the phase's nominal
// professional-share cost from the client-added anchor year to the row's
// calendar position. Annual-stepped: a row's cost holds flat across the
// 12 months following each integer-year anniversary. Strategies that
// need a different cost model can reimplement.
//
// Single-anchor formulation: counts months from the first contributing
// phase's predicted start year along the projection's rounded-month
// clock (`Math.round(phaseCareMonthsNeeded)` for each prior phase plus
// `monthsIntoPhase`). The earlier per-phase formulation mixed an
// unrounded phase start year with a rounded `monthsIntoPhase`, so each
// phase boundary leaked up to ±½ month into the inflation count.
export function computeMonthlyCostMetrics(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
  monthsIntoPhase: number,
): MonthlyCostMetrics {
  const phaseCosts = client.phaseCosts?.find(p => p.carePhase === phaseCarePlan.carePhase);
  const nominalPhaseCost = phaseCosts?.phaseProfessionalShareCost ?? 0;
  const phaseMonths = Math.max(Math.round(phaseCarePlan.phaseCareMonthsNeeded), 1);
  const nominalCareCost = nominalPhaseCost / phaseMonths;
  const inflationRate = appliedHealthCareInflation(client);

  const currentYear = getCareCostAnchorYear(client);
  const firstPhaseStartYear = getFirstContributingPhaseStartYear(client);

  const phasePlans = client.projectionInputs?.phaseCarePlans ?? [];
  const phaseIdx = phasePlans.findIndex(p => p.carePhase === phaseCarePlan.carePhase);
  const monthsBeforeThisPhase = phasePlans
    .slice(0, Math.max(phaseIdx, 0))
    .reduce((sum, p) => sum + Math.round(p.phaseCareMonthsNeeded), 0);
  const monthIndexWithinClient = monthsBeforeThisPhase + monthsIntoPhase;

  const yearsFromTodayContinuous =
    (firstPhaseStartYear - currentYear) + monthIndexWithinClient / 12;
  const yearsFromToday = Math.max(Math.floor(yearsFromTodayContinuous), 0);

  const effectiveCareCost = calcMonthlyCareInflatedValue(
    nominalCareCost,
    inflationRate,
    yearsFromToday * 12,
  );
  return {
    nominalCareCost,
    effectiveCareCost,
    dailyCareCost: effectiveCareCost / DAYS_IN_MONTH,
    careCostInflationYears: yearsFromToday,
    projectedCareYear: Math.floor(firstPhaseStartYear + monthIndexWithinClient / 12),
  };
}
